// @flow
import React, { useContext, useEffect } from 'react';
import classnames from 'classnames';
import { get } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';

import { useRouter } from 'Client/router/useRouter';
import { COLORS } from 'Components/utils/color/colors';
import { Button } from 'Components/frameworks/button/Button.jsx';
import { getAllAttendeesIds } from 'Components/widgets/contact-info/ContactInfoUtils';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer.jsx';
import { ICONS } from 'Components/utils/icon/icons';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { truncate } from 'Components/utils/string/string.util';
import { CriteriaTransport } from 'Components/widgets/criteria/criteria-transport/CriteriaTransport.jsx';
import { CriteriaContext } from 'Components/widgets/criteria/CriteriaContext';
import {
  ACTIONS,
  useCriteriaDisplayContext,
} from 'Components/widgets/criteria/CriteriaDisplayContext';
import { CriteriaWhen } from 'Components/widgets/criteria/criteria-when/CriteriaWhen.jsx';
import { CriteriaWho } from 'Components/widgets/criteria/criteria-who/CriteriaWho.jsx';
import { PriceRemoteContext } from 'Components/widgets/price-remote/PriceRemoteContext';
import ReactAppContext from 'Context/ReactAppContext';
import { ReactPageContext } from 'Context/pageContext';
import { useSetUrlWithFacetings } from 'Src/hooks/use-set-url-with-facetings/useSetUrlWithFacetings';
import { t } from 'Src/i18n';
import { BOOKING_ENGINE_ENTRIES } from 'Store/booking/constants';
import { fetchProposalsAndRedirect } from 'Store/booking/proposals/proposals.action';
import { fetchCalendar } from 'Store/calendar/calendar.action';
import { changeDepartureCity, setKidsValidationState } from 'Store/faceting/facetingSearch.action';
import { getPricesByDays } from 'Store/calendar/calendar.selector';
import {
  getDepartureDate,
  getTripDuration,
  getDepartureCity,
} from 'Store/trip-criteria/tripCriteria.selector';
import { type PricesByDays } from 'Store/calendar/calendar.helper';
import { type Adults } from 'Store/trip-information/adults/adults.reducer';
import { type Kids } from 'Store/trip-information/kids/kids.reducer';
import { getAdults, getAdultsNumber, getKids } from 'Store/trip-information/storeHelper';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { timestampToApiDate } from 'Utils/date/apiDate';
import { DEFAULT_DEPARTURE_CITY_VALUE } from 'Utils/faceting/constants.js';
import { invalidateNotValidatedKids } from 'Utils/faceting/kidsValidationStateUtils';
import { isSeller } from 'Utils/seller/sellerUtils';
import { convertLocationToURI } from 'Utils/uri/uriUtils';
import { PRICE_REMOTE_FORM_ID } from 'Components/widgets/price-remote/constants.js';

// TODO: A sortir dans un fichier, mutaliser avec SearchForm
const SubmitButton = ({
  label,
  isStickyAndIsDesktop,
}: {
  label?: string,
  isStickyAndIsDesktop: boolean,
}) => {
  const { isDesktop } = useContext(ResponsiveContext);
  const stickyAndDesktopWidth = isStickyAndIsDesktop ? 180 : 250;
  const width = isDesktop ? stickyAndDesktopWidth : '100%';
  return (
    <Button
      className="flex flex-1 justify-center items-center px-1 lg:px-0 max-w-full"
      style={{ minWidth: 80, height: 52, width }}
      color={COLORS.WHITE}
      colorBg={COLORS.SUCCESS}
      colorBorder={COLORS.SUCCESS}
      minWidth="initial"
      size="large"
      type="submit"
    >
      {(label && truncate(label, 40)) || <Icon name={ICONS.SEARCH_GLASS} />}
    </Button>
  );
};

export type Props = {
  className?: string,
  durationOptions: Array<number>,
  departureCities: DepartureCities,
  initialTransport?: string,
  isJoyview?: boolean,
  productId: string,
  isStickyAndIsDesktop: boolean,
  urgentisationRate: number,
};

type SelectorState = {
  adults: ?$ReadOnlyArray<Adults>,
  adultsNumber: ?number,
  departureCity: ?string,
  departureDate: ?number,
  duration: ?number,
  kids: ?$ReadOnlyArray<Kids>,
  kidsValidationState: ?Array<ValidationState>,
  prices: ?PricesByDays,
};

const selector = (state: ReduxState): SelectorState => {
  return {
    adults: getAdults(state),
    adultsNumber: getAdultsNumber(state),
    departureCity: getDepartureCity(state),
    departureDate: getDepartureDate(state),
    duration: getTripDuration(state),
    kids: getKids(state),
    kidsValidationState: get(state, 'faceting.kidsValidationState'),
    prices: getPricesByDays(state),
  };
};

export const PriceRemoteForm = ({
  className,
  departureCities,
  durationOptions,
  initialTransport,
  isJoyview,
  productId,
  isStickyAndIsDesktop,
  urgentisationRate,
}: Props) => {
  const appContext = useContext(ReactAppContext);
  const pageContext = useContext(ReactPageContext);

  const [priceRemoteContext, setPriceRemoteContext] = useContext(PriceRemoteContext);
  const [criteriaContext] = useContext(CriteriaContext);
  const [, dispatchCriteriaDisplayContext] = useCriteriaDisplayContext();
  const {
    adults,
    adultsNumber,
    departureCity,
    departureDate,
    duration,
    kids,
    kidsValidationState,
    prices,
  } = useSelector(selector);
  const dispatch = useDispatch();
  const { location } = useRouter<ProductUrlParams, ProductQueryParams>();
  const track = useTracking();

  const isReadyForBookingEngine =
    criteriaContext.criteriaWhenIsSet &&
    criteriaContext.criteriaWhoIsSet &&
    criteriaContext.criteriaTransportIsSet;

  const uri = convertLocationToURI(location);
  useSetUrlWithFacetings({
    adultsNumber,
    departureCity,
    departureDate,
    duration,
    kids,
    kidsValidationState,
    uri,
  });

  const changeCity = (value: string, sendGAEvent: boolean = true) => {
    dispatchCriteriaDisplayContext({
      type: ACTIONS.CLOSE_CRITERIA,
      payload: 'criteriaTransport',
    });
    dispatch(changeDepartureCity(value));

    if (sendGAEvent) {
      track({
        actionGA: 'priceBar',
        labelGA: `departureCity_${value}`,
        categoryGA: pageContext.pageId,
      });
    }
  };

  useEffect(
    () => {
      const isDepartureCityAvailable = departureCities.find(({ value }) => value === departureCity);

      if (initialTransport && (!departureCity || !isDepartureCityAvailable)) {
        changeCity(initialTransport, initialTransport !== DEFAULT_DEPARTURE_CITY_VALUE);
      }
    },
    [initialTransport],
  );

  useEffect(
    () => {
      dispatch(
        fetchCalendar(
          {
            resortId: productId,
            duration: durationOptions[0],
            transport: departureCity || initialTransport,
            urgentisationRate,
          },
          appContext,
        ),
      );
    },
    [departureCity, productId],
  );

  const handleMandatoryFields = () => {
    if (!departureDate || !duration) {
      setPriceRemoteContext({ isCompact: false });
      dispatchCriteriaDisplayContext({
        type: ACTIONS.OPEN_CRITERIA,
        payload: 'criteriaWhen',
      });
    } else if (!criteriaContext.criteriaWhoIsSet) {
      const validationState = invalidateNotValidatedKids(kidsValidationState);
      dispatch(setKidsValidationState(validationState));
      setPriceRemoteContext({ isCompact: false });
      dispatchCriteriaDisplayContext({
        type: ACTIONS.OPEN_CRITERIA,
        payload: 'criteriaWho',
      });
    }

    if (!departureCity) {
      changeCity(DEFAULT_DEPARTURE_CITY_VALUE);
    }
  };

  const onSubmit = (event: ?Event) => {
    if (event) {
      event.preventDefault();
    }
    handleMandatoryFields();

    track({
      actionGA: 'priceBar',
      labelGA: `${isReadyForBookingEngine ? 'continue' : 'organise_your_holiday'}`,
      categoryGA: pageContext.pageId,
    });

    if (isReadyForBookingEngine) {
      const departureCityOption = departureCities.find(city => city.value === departureCity);
      const trackingParams = {
        departureCityName: departureCityOption ? departureCityOption.label : undefined,
        shouldTrackTransport: true,
      };
      const newArrivalDate = timestampToApiDate(departureDate);
      const fetchParams = {
        // $FlowFixMe
        ...(newArrivalDate ? { newArrivalDate } : null),
        departureCity,
        duration: duration || 7,
        globalLoaderLabel: t('booking.form.proposalsLoaderLabel'),
        isJoyView: isJoyview,
        productId,
        bookingEngineEntry: BOOKING_ENGINE_ENTRIES.PRICE_REMOTE,
        ...(isSeller(appContext) ? getAllAttendeesIds({ adults, kids }) : null),
      };

      dispatch(fetchProposalsAndRedirect(fetchParams, trackingParams, appContext, location));
    }
  };

  useEffect(
    () => {
      setPriceRemoteContext({ submitPriceRemoteForm: onSubmit });
    },
    [isReadyForBookingEngine],
  );

  return (
    <form className={className} id={PRICE_REMOTE_FORM_ID} onSubmit={onSubmit}>
      <SiteContainer>
        <div>
          <div className="lg:flex lg:flex-row">
            <div
              className={classnames({
                'flex -mx-1 lg:mx-0 lg:flex-2': !priceRemoteContext.isCompact,
                hidden: priceRemoteContext.isCompact,
              })}
            >
              <div className="flex-1 lg:mr-2 px-1 lg:px-0" data-testid="search-form-criteria-when">
                <CriteriaWhen
                  defaultDuration={durationOptions[0]}
                  durationOptions={durationOptions}
                  prices={prices}
                />
              </div>
              <div className="flex-1 lg:mx-2 px-1 lg:px-0" data-testid="search-form-criteria-who">
                <CriteriaWho isKidBirthdateRequired />
              </div>
              <div
                className="flex-1 lg:mx-2 px-1 lg:px-0"
                data-testid="search-form-criteria-transport"
              >
                <CriteriaTransport departureCitiesOptions={departureCities} />
              </div>
            </div>

            <div className="mt-2 lg:mt-0 lg:ml-2 max-w-full">
              <SubmitButton
                label={
                  isReadyForBookingEngine
                    ? t('common.continue')
                    : t('priceRemote.priceRemoteButton')
                }
                isStickyAndIsDesktop={isStickyAndIsDesktop}
              />
            </div>
          </div>
        </div>
      </SiteContainer>
    </form>
  );
};
